<script lang="ts">
export default {
  name: 'PolicySublimits',
};
</script>

<script setup lang="ts">
import { computed } from 'vue';
import { PolicySublimit, PolicySubretention } from '@/types/PolicySublimit';
import { compactMoney } from '@/utilities/Money';
import { Pencil } from '@lucide/vue';

const emit = defineEmits<{
  (e: 'edit-in-modal', policyLineId: number | undefined): void;
}>();

const props = defineProps<{
  coverageType: string;
  sublimits: PolicySublimit[];
  subretentions?: PolicySubretention[];
  canEdit?: boolean;
}>();

const subretentionRows = computed(() => props.subretentions ?? []);

// Every sublimit on a policy belongs to a line; the first one is enough to open the edit modal
// on the right record. Falls back to the subretentions when a policy has only those.
const editablePolicyLineId = computed(() => props.sublimits[0]?.policyLineId ?? subretentionRows.value[0]?.policyLineId);
</script>

<template>
  <div class="flex flex-col gap-4">
    <div
      v-if="canEdit"
      class="flex justify-end">
      <button
        class="btn btn-sm btn-primary"
        data-testid="edit-sublimits"
        @click="emit('edit-in-modal', editablePolicyLineId)">
        <Pencil :size="14" />
        Edit sublimits &amp; subretentions
      </button>
    </div>

    <div class="rounded-box border border-base-content/15 bg-base-100 p-0.5">
      <table
        id="policy-sublimits"
        class="table-pin-rows table overflow-auto rounded-box table-zebra">
        <thead>
          <tr>
            <td>Sublimit Description</td>
            <td class="pr-9 text-right">Limit</td>
          </tr>
        </thead>

        <tbody>
          <tr
            v-for="sublimit in sublimits"
            :key="sublimit.id"
            class="hover:bg-gray-50">
            <td>{{ sublimit.name }}</td>
            <td
              class="w-50 pr-9 text-right"
              :data-raw="sublimit.value">
              {{ compactMoney(sublimit.value) }}
            </td>
          </tr>
          <tr v-if="sublimits.length === 0">
            <td
              colspan="2"
              class="text-center text-sm text-slate-medium">
              No sublimits on this policy.
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <div
      v-if="subretentionRows.length > 0"
      class="rounded-box border border-base-content/15 bg-base-100 p-0.5">
      <table
        id="policy-subretentions"
        class="table-pin-rows table overflow-auto rounded-box table-zebra">
        <thead>
          <tr>
            <td>Subretention Description</td>
            <td>Basis</td>
            <td class="pr-9 text-right">Retention</td>
          </tr>
        </thead>

        <tbody>
          <tr
            v-for="subretention in subretentionRows"
            :key="subretention.id"
            class="hover:bg-gray-50">
            <td>{{ subretention.name }}</td>
            <td :data-raw="subretention.basis ?? ''">{{ subretention.basis || '—' }}</td>
            <td
              class="w-50 pr-9 text-right"
              :data-raw="subretention.value">
              {{ compactMoney(subretention.value) }}
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>
